// agent-frontend/src/components/fieldStyles.ts
// ─────────────────────────────────────────────────────────────────
// Phase R7 follow-up (2026-05-13): single source of truth for the
// agent-frontend form-field look. Mirrors the admin-frontend
// fieldStyles module added one phase earlier.
//
// Brand context
//   The agent-frontend uses the "arena" + "neon" colour scales from
//   the tailwind config (see tailwind.config.cjs in this folder).
//   Surfaces that are domain-specific often pick a different focus
//   accent — e.g. recharge forms use amber, withdrawal forms use
//   amber, partnership/admin-style screens use violet, profile uses
//   brand. The canonical INPUT_CLS below ships with VIOLET as the
//   default because that's what the majority of admin-style pages
//   already use (AdminUserDetail, PlayersListPanel, etc.).
//
// To use a different focus accent on a specific form, compose:
//   import { INPUT_CLS } from '@/components/fieldStyles';
//   // …or relative path depending on file location
//   <input className={INPUT_CLS.replace('violet-400', 'emerald-400')} ... />
// — or write the inline className for that one form.
//
// Variants
//   INPUT_CLS         — default fill-the-row input. Use everywhere
//                       new fields are added.
//   INPUT_SM_CLS      — compact inline (≤80px containers).
//
// All variants:
//   * Respect dark mode automatically.
//   * Explicit focus state (ring + border).
//   * Generous padding so 14px text is readable on touch screens.
// ─────────────────────────────────────────────────────────────────

export const INPUT_CLS =
  'w-full rounded-xl px-3 py-2 text-[14px] ' +
  'bg-white dark:bg-arena-700/40 ' +
  'border border-arena-200/60 dark:border-arena-600/50 ' +
  'text-arena-900 dark:text-white ' +
  'placeholder-arena-400 dark:placeholder-neon-200/55 ' +
  'focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

// 2026-05-18 — Date inputs need a tighter max-width because (a) the
// date string is fixed-length so a full-row input looks oversized on
// every breakpoint and (b) on iOS Safari the native control pads
// itself with the right-edge calendar icon, making a w-full date
// field overwhelm narrow mobile viewports.
//
// 12rem ≈ 192px comfortably fits "DD/MM/YYYY" + native picker icon
// at 14px font; bumped to 14rem on sm+ so it doesn't look cramped
// on tablets.
export const INPUT_DATE_CLS =
  'rounded-xl px-3 py-2 text-[14px] ' +
  'w-full max-w-[10rem] sm:max-w-[12rem] ' +
  'bg-white dark:bg-arena-700/40 ' +
  'border border-arena-200/60 dark:border-arena-600/50 ' +
  'text-arena-900 dark:text-white ' +
  'placeholder-arena-400 dark:placeholder-neon-200/55 ' +
  'focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

export const INPUT_SM_CLS =
  'rounded-lg px-2 py-1 text-[12.5px] ' +
  'bg-white dark:bg-arena-700/40 ' +
  'border border-arena-200/60 dark:border-arena-600/50 ' +
  'text-arena-900 dark:text-white ' +
  'placeholder-arena-400 dark:placeholder-neon-200/55 ' +
  'focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}
